import React from "react";
import { useDraggable } from "@dnd-kit/core";
import { MdClose, MdCropSquare, MdFilterNone, MdOpenInNew, MdRemove } from "react-icons/md";
import "./DesktopWindow.css";

function DesktopWindow({
  id,
  title,
  icon,
  accentColor,
  position,
  size,
  zIndex,
  minY,
  isMinimized,
  isMaximized,
  onClose,
  onFocus,
  onResize,
  onMinimize,
  onMaximizeToggle,
  onPopOut,
  children,
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `window-${id}`,
  });

  // Clamp the live drag transform (not just the position committed on drop) so the
  // window can't be visually dragged behind the header even mid-gesture.
  const rawY = position.y + (transform?.y ?? 0);
  const clampedY = minY != null ? Math.max(minY, rawY) : rawY;

  const style = {
    transform: `translate3d(${position.x + (transform?.x ?? 0)}px, ${clampedY}px, 0)`,
    // A minimized window drops its explicit width/height so it shrinks to fit just the
    // titlebar's content (title + controls) instead of staying the full window width.
    width: isMinimized ? undefined : size.width,
    height: isMinimized ? undefined : size.height,
    zIndex,
  };

  const handleResizePointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;

    const handlePointerMove = (moveEvent) => {
      onResize(id, {
        width: startWidth + (moveEvent.clientX - startX),
        height: startHeight + (moveEvent.clientY - startY),
      });
    };

    const handlePointerUp = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`desktop-window${isDragging ? " dragging" : ""}${isMinimized ? " minimized" : ""}${isMaximized ? " maximized" : ""}`}
      onMouseDown={onFocus}
    >
      {accentColor && <div className="desktop-window-accent" style={{ backgroundColor: accentColor }} />}
      <div className="desktop-window-titlebar" {...attributes} {...listeners}>
        <span className="desktop-window-title">
          {icon}
          {title}
        </span>
        <div className="desktop-window-controls">
          {onMinimize && (
            <button
              type="button"
              className="desktop-window-control"
              onClick={(e) => {
                e.stopPropagation();
                onMinimize(id);
              }}
              aria-label={isMinimized ? `Restore ${title}` : `Minimize ${title}`}
              title={isMinimized ? "Restore" : "Minimize"}
            >
              <MdRemove />
            </button>
          )}
          {onMaximizeToggle && (
            <button
              type="button"
              className="desktop-window-control"
              onClick={(e) => {
                e.stopPropagation();
                onMaximizeToggle(id);
              }}
              aria-label={isMaximized ? `Restore ${title}` : `Maximize ${title}`}
              title={isMaximized ? "Restore" : "Maximize"}
            >
              {isMaximized ? <MdFilterNone /> : <MdCropSquare />}
            </button>
          )}
          {onPopOut && (
            <button
              type="button"
              className="desktop-window-control"
              onClick={(e) => {
                e.stopPropagation();
                onPopOut(id);
              }}
              aria-label={`Pop out ${title} into a new window`}
              title="Pop out into a new window"
            >
              <MdOpenInNew />
            </button>
          )}
          <button
            type="button"
            className="desktop-window-control desktop-window-close"
            onClick={(e) => {
              e.stopPropagation();
              onClose(id);
            }}
            aria-label={`Close ${title}`}
            title="Close"
          >
            <MdClose />
          </button>
        </div>
      </div>
      {/* Kept mounted (not unmounted) while minimized so scroll position, filters, and
          in-progress form input inside the window survive minimize/restore. */}
      <div className="desktop-window-body" style={isMinimized ? { display: "none" } : undefined}>
        {children}
      </div>
      {!isMinimized && !isMaximized && (
        <div
          className="desktop-window-resize-handle"
          onPointerDown={handleResizePointerDown}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default DesktopWindow;
